import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Alert,
  Modal,
  Form,
  ListGroup
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import useHeaderGenerator from 'components/app/common/functions/useHeaderGenerator';
import FormModal from 'components/app/common/FormModal';
import ClassService from './ClassService';
import ProgrammingService from 'components/app/wod/programming/ProgrammingService';
import ProgramService from 'components/app/wod/programs/ProgramService';
import schema from './jsonSchema';

const formatDate = dateValue => {
  let date;

  // Handle date as array [year, month, day]
  if (Array.isArray(dateValue)) {
    const [year, month, day] = dateValue;
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateValue);
  }

  // Format as DD/MM/YYYY
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

const toISODate = dateValue => {
  let date;

  // Handle date as array [year, month, day]
  if (Array.isArray(dateValue)) {
    const [year, month, day] = dateValue;
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateValue);
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatTime = timeValue => {
  // If timeValue is already in HH:MM format, return it
  if (typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}$/)) {
    return timeValue;
  }

  // Handle time as array [hours, minutes]
  if (Array.isArray(timeValue)) {
    const [hours, minutes] = timeValue;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}`;
  }

  // Otherwise, try to parse it as a date/time
  const date = new Date(timeValue);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

const WODClassDetails = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const [modalShow, setModalShow] = useState(false);
  const [event, setEvent] = useState();
  const [program, setProgram] = useState();
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [programSearchTerm, setProgramSearchTerm] = useState('');
  const [assigningProgram, setAssigningProgram] = useState(false);

  const showValue = attributeName => {
    return event[attributeName];
  };
  const refreshProgramDetails = classData => {
    if (!classData) {
      return;
    }
    if (classData.programIdentifier) {
      ProgramService.retrieveProgram(classData.programIdentifier)
        .then(data => {
          setProgram(data);
        })
        .catch(error => {
          console.error('Error retrieving assigned Program for class', error);
          setProgram(undefined);
        });
      return;
    }
    if (!classData.date || !classData.name) {
      return;
    }
    const profileLogged = JSON.parse(localStorage.getItem('profile'));
    const { accountIdentifier } = profileLogged;
    ProgrammingService.retrieveProgram(
      accountIdentifier,
      classData.date,
      classData.name
    )
      .then(data => {
        setProgram(data);
      })
      .catch(error => {
        console.error('Error retrieving Program for class', error);
        setProgram(undefined);
      });
  };
  const refreshClassDetails = () => {
    ClassService.retrieveClass(identifier)
      .then(data => {
        const formattedDate = formatDate(data.date);
        const formattedStartTime = data.startTime
          ? formatTime(data.startTime)
          : '';
        const formattedEndTime = data.endTime ? formatTime(data.endTime) : '';

        const updatedData = {
          ...data,
          date: formattedDate,
          startTime: formattedStartTime,
          endTime: formattedEndTime
        };
        setEvent(updatedData);
        refreshProgramDetails({ ...updatedData, date: toISODate(data.date) });
      })
      .catch(error => {
        console.error('Error retrieving WODClass', error);
      });
  };
  const handleAddToClass = attendeeIdentifier => {
    ClassService.addToClass(identifier, attendeeIdentifier)
      .then(response => {
        console.log('Added to class:', response);
        refreshClassDetails();
      })
      .catch(error => {
        console.error('Error adding to class', error);
      });
  };
  const handleLeaveWaitlist = attendeeIdentifier => {
    ClassService.leaveWaitlist(identifier, attendeeIdentifier)
      .then(response => {
        console.log('Removed from waitlist:', response);
        refreshClassDetails();
      })
      .catch(error => {
        console.error('Error removing from waitlist', error);
      });
  };
  const handleAssignProgram = () => {
    setProgramSearchTerm('');
    ProgramService.retrieveAllProgramByAccount()
      .then(data => {
        setAvailablePrograms(data || []);
        setShowProgramModal(true);
      })
      .catch(error => {
        console.error('Error retrieving programs', error);
      });
  };
  const handleCloseProgramModal = () => {
    setShowProgramModal(false);
  };
  const handleSelectProgram = selectedProgram => {
    setAssigningProgram(true);
    ClassService.assignProgramToClass(identifier, selectedProgram.identifier)
      .then(() => {
        setAssigningProgram(false);
        setShowProgramModal(false);
        // Refresh the program details right away with the selected program
        // so the "Program" card reflects the new assignment immediately,
        // then refresh the class (and program) details from the backend.
        setProgram(selectedProgram);
        refreshClassDetails();
      })
      .catch(error => {
        console.error('Error assigning program to class', error);
        setAssigningProgram(false);
      });
  };
  const filteredPrograms = availablePrograms.filter(p => {
    if (!programSearchTerm) {
      return true;
    }
    const term = programSearchTerm.toLowerCase();
    return (
      (p.name && p.name.toLowerCase().includes(term)) ||
      (p.type && p.type.toLowerCase().includes(term)) ||
      (p.description && p.description.toLowerCase().includes(term)) ||
      (p.date &&
        (Array.isArray(p.date) ? toISODate(p.date) : String(p.date))
          .toLowerCase()
          .includes(term))
    );
  });
  const handleCancelBooking = attendeeIdentifier => {
    console.log(
      'Cancelling booking for class:',
      identifier,
      'and profile:',
      attendeeIdentifier
    );
    ClassService.removeBooking(identifier, attendeeIdentifier)
      .then(response => {
        console.log('Booking cancelled:', response);
        refreshClassDetails();
      })
      .catch(error => {
        console.error('Error cancelling booking', error);
      });
  };
  useEffect(() => {
    refreshClassDetails();
  }, [identifier]);

  const buttonsConf = [
    {
      name: 'Edit Class',
      type: 'button',
      variant: 'info',
      onClick: () => {
        console.log('Edit this event');
        navigate('/app/wod/admin/classes/' + identifier + '/edit');
      }
    },
    {
      name: 'Assign Program',
      type: 'button',
      variant: 'success',
      onClick: () => {
        console.log('Assign program to this event');
        handleAssignProgram();
      }
    }
  ];

  const jsonSchema = schema;
  const headerGenerated = useHeaderGenerator('WODClass', ' ', buttonsConf);

  return (
    <>
      {headerGenerated}
      {modalShow && (
        <FormModal
          message={'WODClass has been updated!'}
          modalShow={modalShow}
          setModalShow={setModalShow}
        />
      )}
      {event && (
        <>
          <Card>
            <Card.Body>
              {jsonSchema.map(field => {
                if (field.type === 'group') {
                  return field.fields.map(f => (
                    <Row key={f.name}>
                      <Col xs={5} sm={4}>
                        <p className="fw-semi-bold mb-1">{f.label + ': '}</p>
                      </Col>
                      <Col>{showValue(f.name)}</Col>
                    </Row>
                  ));
                } else {
                  return (
                    <Row key={field.name}>
                      <Col xs={5} sm={4}>
                        <p className="fw-semi-bold mb-1">
                          {field.label + ': '}
                        </p>
                      </Col>
                      <Col>{showValue(field.name)}</Col>
                    </Row>
                  );
                }
              })}
            </Card.Body>
          </Card>
          <Card className="mt-2">
            <Card.Header>Program</Card.Header>
            <Card.Body>
              {program ? (
                <div
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                  className="pe-2"
                >
                  <Row>
                    <Col xs={5} sm={4}>
                      <p className="fw-semi-bold mb-1">Name: </p>
                    </Col>
                    <Col>{program.name}</Col>
                  </Row>
                  <Row>
                    <Col xs={5} sm={4}>
                      <p className="fw-semi-bold mb-1">Source: </p>
                    </Col>
                    <Col>{program.source}</Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <p className="fw-semi-bold mb-1">Strength: </p>
                      <p style={{ whiteSpace: 'pre-wrap' }}>
                        <div dangerouslySetInnerHTML={{ __html: program.strength }} />
                      </p>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <p className="fw-semi-bold mb-1">Workout: </p>
                      <p style={{ whiteSpace: 'pre-wrap' }}>
                        <div dangerouslySetInnerHTML={{ __html: program.workout }} />
                      </p>
                    </Col>
                  </Row>
                </div>
              ) : (
                <Alert variant="warning">
                  <p>
                    No program assigned to this class. Please assign a program
                    to this class to view the details.
                  </p>
                </Alert>
              )}
            </Card.Body>
          </Card>
          <Row>
            <Col>
              <Card className="mt-2">
                <Card.Header className="bg-transparent border-bottom border-secondary">
                  <h5 className="mb-0 fw-bold">Attendees</h5>
                </Card.Header>
                <Card.Body>
                  {event.attendees && event.attendees.length > 0 ? (
                    event.attendees.map(attendee => (
                      <div
                        className="d-flex align-items-center mb-2"
                        key={attendee.identifier}
                      >
                        <Link to={`/app/wod/members/${attendee.identifier}`}>
                          {attendee.firstName} {attendee.lastName}
                        </Link>
                        <Button
                          variant="danger"
                          className="btn btn-sm ms-auto"
                          size="sm"
                          onClick={() =>
                            handleCancelBooking(attendee.identifier)
                          }
                        >
                          Cancel
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p>No attendees found.</p>
                  )}
                </Card.Body>
              </Card>
              <Card className="mt-2">
                <Card.Header className="bg-transparent border-bottom border-secondary">
                  <h5 className="mb-0 fw-bold">Waitlisted</h5>
                </Card.Header>
                <Card.Body>
                  {event.waitlisted && event.waitlisted.length > 0 ? (
                    event.waitlisted.map(waitlisted => (
                      <div
                        className="d-flex align-items-center mb-2"
                        key={waitlisted.identifier}
                      >
                        <Link to={`/app/wod/members/${waitlisted.identifier}`}>
                          {waitlisted.firstName} {waitlisted.lastName}
                        </Link>
                        <Button
                          variant="success"
                          className="btn btn-sm ms-auto"
                          size="sm"
                          onClick={() =>
                            handleAddToClass(waitlisted.identifier)
                          }
                        >
                          Add to class
                        </Button>
                        <Button
                          variant="danger"
                          className="btn btn-sm ms-auto"
                          size="sm"
                          onClick={() => {
                            console.log('Remove from waitlist');
                            handleLeaveWaitlist(waitlisted.identifier);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p>No waitlisted individuals found.</p>
                  )}
                </Card.Body>
              </Card>
              <Card className="mt-2">
                <Card.Header className="bg-transparent border-bottom border-secondary">
                  <h5 className="mb-0 fw-bold">Cancellations</h5>
                </Card.Header>
                <Card.Body>
                  {event.cancellations && event.cancellations.length > 0 ? (
                    event.cancellations.map(cancellation => (
                      <div
                        className="d-flex align-items-center mb-2"
                        key={cancellation.identifier}
                      >
                        <Link
                          to={`/app/wod/members/${cancellation.identifier}`}
                        >
                          {cancellation.firstName} {cancellation.lastName}
                        </Link>
                        <Button
                          variant="success"
                          className="btn btn-sm ms-auto"
                          size="sm"
                          onClick={() =>
                            handleAddToClass(cancellation.identifier)
                          }
                        >
                          Add to class
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p>No cancellations found.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
      <Modal show={showProgramModal} onHide={handleCloseProgramModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Program</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="programSearch">
            <Form.Control
              type="text"
              placeholder="Search by date, name, type or description"
              value={programSearchTerm}
              onChange={e => setProgramSearchTerm(e.target.value)}
            />
          </Form.Group>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredPrograms.length > 0 ? (
              <ListGroup>
                {filteredPrograms.map(p => (
                  <ListGroup.Item
                    key={p.identifier}
                    action
                    disabled={assigningProgram}
                    onClick={() => handleSelectProgram(p)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="fw-semi-bold mb-1">{p.name}</p>
                        <p className="mb-0 text-muted">
                          {p.type} -{' '}
                          {Array.isArray(p.date) ? toISODate(p.date) : p.date}
                        </p>
                        {p.description && (
                          <p className="mb-0 text-muted">{p.description}</p>
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              <Alert variant="warning">No programs found.</Alert>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseProgramModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WODClassDetails;
